import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Snackbar,
  Alert,
  IconButton
} from '@mui/material';
import {
  ContentCopy,
  QrCode2,
  Close
} from '@mui/icons-material';

function InviteCodeCard({ familyData }) {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const inviteCode = familyData?.inviteCode || '--------'; // 8 characters now

  // Check localStorage for dismissed state
  useEffect(() => {
    const dismissed = localStorage.getItem('inviteCardDismissed') === 'true';
    setIsDismissed(dismissed);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('inviteCardDismissed', 'true');
    setIsDismissed(true);
  };

  const handleRestore = () => {
    localStorage.setItem('inviteCardDismissed', 'false');
    setIsDismissed(false);
  };

  // If dismissed, show small restore button
  if (isDismissed) {
    return (
      <Card sx={{ bgcolor: 'grey.100' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QrCode2 color="disabled" />
              <Typography variant="body2" color="text.secondary">
                Invite Code Hidden
              </Typography>
            </Box>
            <Button size="small" onClick={handleRestore}>
              Show Code
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            You can find this code in your profile settings
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const handleCopyCode = () => {
    navigator.clipboard.writeText(inviteCode);
    setSnackbarOpen(true);
  };

  return (
    <>
      <Card
        sx={{
          background: 'linear-gradient(135deg, #8E24AA 0%, #00BCD4 100%)',
          color: 'white',
          boxShadow: 3
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QrCode2 />
              <Typography variant="h6" fontWeight="bold">
                Family Invite Code
              </Typography>
            </Box>
            <IconButton 
              size="small" 
              onClick={handleDismiss}
              sx={{ color: 'white' }}
              title="Hide this card (can restore later)"
            >
              <Close />
            </IconButton>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
            Share this code with family members so they can join:
          </Typography>

          <Box
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              mb: 2
            }}
          >
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{
                letterSpacing: '0.3em',
                fontFamily: 'monospace'
              }}
            >
              {inviteCode}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<ContentCopy />}
              onClick={handleCopyCode}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                }
              }}
            >
              Copy Code
            </Button>
          </Box>

          <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ display: 'block' }}>
              💡 <strong>How family members join:</strong>
            </Typography>
            <Typography variant="caption">
              <strong>Parents:</strong> Sign up → Join Family → Enter code<br />
              <strong>Children:</strong> Child Access → Enter code: <strong>{inviteCode}</strong><br />
              (Children login with family code + PIN, no email needed!)
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          ✅ Invite code copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
}

export default InviteCodeCard;


